import { createContext, PropsWithChildren, useCallback, useEffect, useState } from 'react';
import { useMutation } from 'react-query';

import { useProfile } from '@src/features/profile/contexts/profile-context/use-profile';
import { useLocale } from '@src/hooks/use-locale';
import { guestApi } from '@src/services';

type ConsentStatus = Record<string, boolean> & {
  Segment?: boolean;
};

type UserConsentContextType = {
  showConsentModal: VoidFunction;
  consentStatus: ConsentStatus;
};

export const UserConsentContext = createContext<UserConsentContextType>({
  showConsentModal: () => null,
  consentStatus: {},
});

export const UserConsentContextProvider = ({ children }: PropsWithChildren) => {
  const [consentStatus, setConsentStatus] = useState<ConsentStatus>({});
  const profile = useProfile();

  const locale = useLocale();
  const { mutate: postGuestFirstPartyConsent } = useMutation('post-guest-consents-on-uc-event', (email: string) =>
    guestApi.postGuestConsents(
      {
        consents: [{ type: 'first-party' }],
        preferredLanguage: locale,
      },
      { params: { email } },
    ),
  );

  const showConsentModal = useCallback(() => {
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    (window as any).UC_UI?.showSecondLayer?.();
  }, []);

  useEffect(() => {
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    const onUserCentricsEvent = (e: any) => {
      const { detail } = e;
      if (!detail) {
        return;
      }

      // eslint-disable-next-line @typescript-eslint/no-unused-vars
      const { event, action, type, ...currentConsent } = detail;
      if (event !== 'consent_status') {
        return;
      }

      setConsentStatus(currentConsent);

      if (!['onAcceptAllServices', 'onDenyAllServices', 'onUpdateServices'].includes(action)) {
        return;
      }

      // eslint-disable-next-line @typescript-eslint/no-explicit-any
      const hasUcConsentedToMarketing = (currentConsent.ucCategory as any)?.marketing === true;

      if (hasUcConsentedToMarketing && profile?.data?.email) {
        postGuestFirstPartyConsent(profile.data.email);
      }
    };

    window.addEventListener('ucEvent', onUserCentricsEvent);

    return () => {
      window.removeEventListener('ucEvent', onUserCentricsEvent);
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  return (
    <UserConsentContext.Provider
      value={{
        showConsentModal,
        consentStatus,
      }}
    >
      {children}
    </UserConsentContext.Provider>
  );
};
